import { Fragment, useEffect, useId, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import { MRT_Localization_DE } from "material-react-table/locales/de";
import { MRT_Localization_EN } from "material-react-table/locales/en";
import { api } from "@/helper/api";

const PAGE_SIZE = 50;

export interface TemplateVariable {
  source: string;
  name: string;
  path: string;
  label: string;
  type: string;
  expression: string;
}

export default function TemplateVariables() {
  const { t, i18n } = useTranslation();
  const paginationLocalization =
    i18n.resolvedLanguage === "en" ? MRT_Localization_EN : MRT_Localization_DE;
  const id = useId();
  const [expanded, setExpanded] = useState(false);
  const [variables, setVariables] = useState<TemplateVariable[]>([]);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState<"loading" | "ready" | "error">(
    "loading"
  );
  const [retry, setRetry] = useState(0);
  const [copyStatus, setCopyStatus] = useState<"success" | "error" | null>(
    null
  );

  useEffect(() => {
    if (!expanded) return;
    const controller = new AbortController();
    setStatus("loading");
    api.genericApiRequest({
      entity: "documentTemplate",
      method: "get",
      additionalRouteParts: { variables: "/" },
      disableGlobalSpinner: true,
      config: { signal: controller.signal },
      successHandler: (response: { data: TemplateVariable[] }) => {
        if (controller.signal.aborted) return;
        setVariables(response.data);
        setPage(0);
        setStatus("ready");
      },
      failHandler: () => {
        if (!controller.signal.aborted) setStatus("error");
      },
    });
    return () => controller.abort();
  }, [expanded, retry]);

  const search = query.trim().toLocaleLowerCase();
  const matches = useMemo(
    () =>
      search
        ? variables.filter((variable) =>
            [variable.path, variable.name, variable.label].some((text) =>
              text.toLocaleLowerCase().includes(search)
            )
          )
        : variables,
    [variables, search]
  );
  const visibleVariables = matches.slice(
    page * PAGE_SIZE,
    (page + 1) * PAGE_SIZE
  );

  const copyExpression = async (expression: string) => {
    try {
      await navigator.clipboard.writeText(expression);
      setCopyStatus("success");
    } catch {
      setCopyStatus("error");
    }
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, value) => setExpanded(value)}
      disableGutters
      elevation={0}
      sx={{ "&::before": { display: "none" } }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        id={`${id}-heading`}
        aria-controls={`${id}-content`}
      >
        {t("template_variables_title")}
      </AccordionSummary>
      {expanded && (
        <AccordionDetails id={`${id}-content`}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t("template_variables_description")}
          </Typography>
          <TextField
            fullWidth
            label={t("template_variables_search")}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(0);
              setCopyStatus(null);
            }}
          />
          {status === "loading" && (
            <CircularProgress
              aria-label={t("template_variables_loading")}
              sx={{ mt: 2 }}
            />
          )}
          {status === "error" && (
            <Alert
              severity="error"
              sx={{ mt: 2 }}
              action={
                <Button onClick={() => setRetry((value) => value + 1)}>
                  {t("template_variables_retry")}
                </Button>
              }
            >
              {t("template_variables_unavailable")}
            </Alert>
          )}
          {copyStatus && (
            <Alert severity={copyStatus} sx={{ mt: 2 }}>
              {t(
                copyStatus === "success"
                  ? "template_variables_copied"
                  : "template_variables_copy_failed"
              )}
            </Alert>
          )}
          {status === "ready" && (
            <>
              {matches.length > PAGE_SIZE && (
                <TablePagination
                  component="div"
                  count={matches.length}
                  page={page}
                  rowsPerPage={PAGE_SIZE}
                  rowsPerPageOptions={[]}
                  onPageChange={(_, value) => setPage(value)}
                  showFirstButton
                  showLastButton
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} ${paginationLocalization.of} ${count}`
                  }
                  getItemAriaLabel={(type) =>
                    ({
                      first: paginationLocalization.goToFirstPage,
                      previous: paginationLocalization.goToPreviousPage,
                      next: paginationLocalization.goToNextPage,
                      last: paginationLocalization.goToLastPage,
                    }[type])
                  }
                />
              )}
              <Box
                key={`${search}:${page}`}
                sx={{ maxHeight: 360, overflowY: "auto", mt: 2 }}
              >
                {!matches.length && (
                  <Typography>{t("template_variables_empty")}</Typography>
                )}
                {visibleVariables.map((variable, index) => (
                  <Fragment key={variable.path}>
                    {(index === 0 ||
                      visibleVariables[index - 1].source !==
                        variable.source) && (
                      <Typography
                        variant="overline"
                        sx={{ display: "block", mt: 1 }}
                      >
                        {t(
                          `template_variables_source_${variable.source}`,
                          variable.source
                        )}
                      </Typography>
                    )}
                    <Box
                      sx={{ py: 1, borderBottom: 1, borderColor: "divider" }}
                    >
                      <Typography>
                        {variable.label} ({variable.type})
                      </Typography>
                      <Typography
                        component="code"
                        variant="body2"
                        sx={{ display: "block", overflowWrap: "anywhere" }}
                      >
                        {variable.expression}
                      </Typography>
                      <Button
                        onClick={() => copyExpression(variable.expression)}
                        aria-label={`${t("template_variables_copy")}: ${
                          variable.path
                        }`}
                      >
                        {t("template_variables_copy")}
                      </Button>
                    </Box>
                  </Fragment>
                ))}
              </Box>
            </>
          )}
        </AccordionDetails>
      )}
    </Accordion>
  );
}
